(ns app.front.view.group.admin.import.common
  (:require [app.domain.group.config.model :as config-model]
            [app.domain.group.model :as group-model]
            [app.domain.property.model :as property-model]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.util :as futil :refer [for-frag]]
            [app.front.view.group.admin.import.subs :as import-subs]
            [app.front.view.group.admin.subs :as gas]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.user.model :as user-model]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(rf/reg-sub ::state
  :-> ::state)

(rf/reg-sub ::users
  :<- [::state]
  (fn [{:keys [users]} _]
    (->> users
         (sort user-model/user-sort-lastname))))

(rf/reg-sub ::root-config
  :<- [::gas/group]
  :<- [::entities/context]
  (fn [[group ctx] _]
    (when-let [permit-type (group-model/primary-permit-type group)]
      (entities/find-entity ctx {:group.config/target permit-type
                                 :group.config/group  (:db/id group)}))))

(rf/reg-sub ::property-tr
  :<- [::gas/group]
  :<- [:tr]
  (fn [[config tr] _]
    (property-model/property-tr-fn tr config)))

(rf/reg-sub ::selectable-categories
  :<- [::root-config]
  :<- [:language]
  (fn [[root-config language] _]
    (->> root-config
         :entity/_parent
         (config-model/sort-group-configs language))))

(rf/reg-sub ::selectable-municipalities
  :<- [::gas/group]
  (fn [group _]
    (let [groups (if (group-model/domain? group)
                   (group-model/descendant-authority-groups group)
                   [group])]
      (->> groups
           (mapcat group-model/scope-municipalities)
           distinct
           (sort-by (comp :localized-string/text :municipality/official-names))))))

(defmulti entities-cols (fn [import-type] import-type))

(defmulti entity-href (fn [import-type _entity] import-type))

(defmulti mapping-settings
  (fn [{:keys [target target-type]}]
    (if target-type
      [target target-type]
      target)))

(defn mapping-line
  "A single configurable row in the mapping UI including header title and input
  with optional children under the header"
  [{:keys [tr title type property-attribute-template-subtype input]} & children]
  [:li.mt-4.flex.flex-col
   [:div.flex
    [:div.w-1-2.border-b.border-solid.mr-4.border-ui-light-gray.h-16.pt-6.flex.flex-col
     [:h4.flex-1 title]
     (when type
       [:span (tr [(keyword (or (namespace type) "data-import-type") (name type))])
        (when property-attribute-template-subtype
          [:<> " (" (tr [(keyword "data-import-type" (name property-attribute-template-subtype))]) ")"])])]
    input]
   (into [:<>] children)])

(defn mapping-details
  "The expandable container for a single import field's mapping settings.
  The settings are given in hiccup format"
  [settings]
  (r/with-let [*expanded? (r/atom false)]
    (let [tr (<sub [:tr])]
      [:div.bg-ui-blue-10.rounded.ml-4.mt-2
       [:div.flex.justify-between.items-center
        [:span.font-bold.pl-4 (tr [:data-import/configuration])]
        (cc/button {:icon             (if @*expanded? i/chevron-up i/chevron-down)
                    :kind             :ghost
                    :icon-description (tr [:data-import/expand-settings])
                    :on-click         #(swap! *expanded? not)}
                   "")]
       [:div.p-4 {:class (when-not @*expanded? "hidden")}
        settings
        [:div.flex.justify-end.pt-4
         (cc/button {:kind             :tertiary
                     :icon-description ""
                     :on-click         #(reset! *expanded? false)}
                    (tr [:data-import/done]))]]])))


(defn mapping-config
  "A single imported field's mapping configuration UI container
  title         The name of the imported field, e.g. \"Application category\"
  select        Hiccup format dropdown for the import file column the field value will be read from
  settings      Hiccup format input(s) for configuring any additional processing of the import file value
  configurable? Indicate configurability of the field when settings are provided"
  [{:keys [tr title type property-attribute-template-subtype select settings configurable?]}]
  [mapping-line
   (util/mmap {:input select} tr title type property-attribute-template-subtype)
   (when (and settings configurable?)
     [mapping-details settings])])

(defn string-input [{:keys [tr settings item->setting item group-id session-id ix mapping-target on-change]}]
  (let [current-value   (get settings (item->setting item))
        has-duplicates? (-> settings vals frequencies (get current-value) (> 1))]
    [ui/text-input
     {:label-text   (tr [:data-import/value-in-file])
      :value        current-value
      :invalid      has-duplicates?
      :invalid-text (tr [:data-import/no-identical-values])
      :on-change    (fn [e]
                      (>evt [::import-subs/set-mapping-debounce
                             {:authority-group-id group-id
                              :session-id         session-id
                              :col-ix             ix
                              :mapping-key        mapping-target
                              :settings           (on-change (futil/tval e))}]))}]))

(defn string-to-value-mapping-settings [mapping-target items-sub item->setting item->text]
  (fn [{:keys [settings session-id group-id ix]}]
    (r/with-let [*settings (r/atom settings)]
      (let [tr     (<sub [:tr])
            draft? (<sub [::import-subs/is-draft? session-id])
            lang   (<sub [:language])]
        [:ul.ml-16
         (for-frag [item (<sub [items-sub session-id settings])]
           (let [value     (get settings (item->setting item))
                 title-val (item->text item lang)
                 title     (if (string? title-val) title-val (tr [title-val]))]
             (when (or draft? value)
               (if draft?
                 [mapping-line
                  (util/mmap
                    {:input [string-input (util/mmap
                                            {:settings  @*settings
                                             :on-change #(swap! *settings assoc (item->setting item) %)}
                                            tr  item->setting item group-id session-id ix mapping-target)]}
                    tr
                    title)]
                 [:div.flex.py-2
                  [:span.font-bold.w-1-2 title]
                  [:span.font-mono value]]))))]))))

(defn get-time-fn
  [time-field]
  (fn [entity]
    (let [group-tz (-> entity group-model/get-authority-group :group/tz)]
      (some-> (get entity time-field)
              (date-util/date-in-tz group-tz)))))
